import { useNavigate } from 'react-router-dom';
import {
  ContestWinnerStatusData,
  ContestWinnerStatusEnum,
  PrizeTypeEnum,
} from '../../../../helpers/utils/types';
import { sanitizeRentDropTerminology } from '../../../../helpers/utils/rentDropTerminology';
import { SKILL_TEST_EXPIRY_BUSINESS_DAYS } from '../../../ContestSkillTest/skillTestUtils';

const WALLET_RENT_CREDITS_PATH = '/wallet#rd-rent-savings';

const TrophyIcon = () => (
  <svg width='22' height='22' viewBox='0 0 24 24' fill='none' aria-hidden='true'>
    <path
      d='M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0V4z'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M7 6H5a3 3 0 003 3M17 6h2a3 3 0 01-3 3'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const formatPrizeLabel = (prizeName?: string): string | undefined => {
  if (!prizeName) return undefined;
  return sanitizeRentDropTerminology(prizeName);
};

const getAwardedCta = (prizeTypeName?: PrizeTypeEnum): string => {
  if (prizeTypeName === PrizeTypeEnum.Ticket) {
    return 'See my tickets';
  }
  return 'See my rent credit';
};

const getBannerCopy = (winnerStatus: ContestWinnerStatusData) => {
  const prizeLabel = formatPrizeLabel(winnerStatus.prizeName);
  const { status, prizeTypeName } = winnerStatus;

  if (status === ContestWinnerStatusEnum.Pending) {
    return {
      title: 'You won the latest rent drop!',
      sub: prizeLabel
        ? `You were selected for ${prizeLabel}. Check your email for the skill-testing question: you have ${SKILL_TEST_EXPIRY_BUSINESS_DAYS} business days to claim it.`
        : `Check your email for the skill-testing question. You have ${SKILL_TEST_EXPIRY_BUSINESS_DAYS} business days to claim your rent drop.`,
      cta: null as string | null,
      tone: 'win' as const,
      prizeLabel,
    };
  }

  if (status === ContestWinnerStatusEnum.Awarded) {
    const awardedSub =
      prizeTypeName === PrizeTypeEnum.Ticket
        ? prizeLabel
          ? `${prizeLabel} added to your balance, keep playing for bigger drops.`
          : 'Bonus tickets have been added to your balance.'
        : prizeLabel
          ? `Your ${prizeLabel} is ready and will apply toward rent.`
          : 'Your rent credit is ready and will apply toward rent.';

    return {
      title: 'You won the latest rent drop!',
      sub: awardedSub,
      cta: getAwardedCta(prizeTypeName),
      tone: 'win' as const,
      prizeLabel,
    };
  }

  if (
    status === ContestWinnerStatusEnum.Expired ||
    status === ContestWinnerStatusEnum.Forfeited ||
    status === ContestWinnerStatusEnum.Cancelled
  ) {
    return {
      title: 'You were selected in the latest rent drop',
      sub: prizeLabel
        ? `Your claim for ${prizeLabel} has closed. Keep earning tickets for the next drop.`
        : 'Your claim window has closed. Keep earning tickets for the next drop.',
      cta: 'Earn more tickets',
      tone: 'soft' as const,
      prizeLabel,
    };
  }

  return {
    title: 'You won the latest rent drop!',
    sub: prizeLabel
      ? `Congratulations, you won ${prizeLabel}.`
      : 'Congratulations, you were selected as a winner.',
    cta: 'View rent drops',
    tone: 'win' as const,
    prizeLabel,
  };
};

type ContestWinBannerProps = {
  winnerStatus: ContestWinnerStatusData;
};

export const ContestWinBanner = ({ winnerStatus }: ContestWinBannerProps) => {
  const navigate = useNavigate();
  const copy = getBannerCopy(winnerStatus);

  const handleCta = () => {
    if (winnerStatus.status === ContestWinnerStatusEnum.Awarded) {
      if (winnerStatus.prizeTypeName === PrizeTypeEnum.Ticket) {
        navigate('/rewards');
        return;
      }
      navigate(WALLET_RENT_CREDITS_PATH);
      return;
    }

    if (
      winnerStatus.status === ContestWinnerStatusEnum.Expired ||
      winnerStatus.status === ContestWinnerStatusEnum.Forfeited ||
      winnerStatus.status === ContestWinnerStatusEnum.Cancelled
    ) {
      navigate('/rewards');
      return;
    }

    navigate('/rent-drop');
  };

  return (
    <div className={`contest-win-banner contest-win-banner--${copy.tone}`} role='status'>
      <div className='contest-win-banner-glow' aria-hidden='true' />
      <div className='contest-win-banner-shimmer' aria-hidden='true' />
      <div className='contest-win-banner-body'>
        <div className='contest-win-banner-icon' aria-hidden='true'>
          <TrophyIcon />
        </div>
        <div className='contest-win-banner-copy'>
          <div className='contest-win-banner-meta'>
            <span className='contest-win-banner-pill'>Winner</span>
            {copy.prizeLabel ? (
              <span className='contest-win-banner-prize'>{copy.prizeLabel}</span>
            ) : null}
          </div>
          <h2 className='contest-win-banner-title'>{copy.title}</h2>
          <p className='contest-win-banner-sub'>{copy.sub}</p>
        </div>
        {copy.cta ? (
          <button type='button' className='contest-win-banner-btn' onClick={handleCta}>
            {copy.cta}
          </button>
        ) : null}
      </div>
    </div>
  );
};
